/* ManageMyUserComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageMyUserRenderer");
const GSO_ID = "565a65db-54e7-4461-a954-f0f38f310e19";
const DEFAULT_COLORS = {hover: "#e7e7e6", selected: "#cde3ee"};
const SAFE_PALETTE = ["#FFA500", "#E100FF", "#F41F1F", "#90EE90", "#24EBEB", "#FFC0CB"];

class ManageMyUserComponent extends EventEmitter {
    constructor(
        userService,
        modalUserFeedback,
        router,
        MUF,
        ControlUser,
        storageFactory,
        systemConfig,
        ActionGroup,
        Credentials
    ) {
        super();
        this._userService = userService;
        this._router = router;
        this._currentUserData = null;
        this._saveModel = {
            user: {},
            controlUser: {},
        };
        this._userImage = undefined;
        this._muf = MUF;
        this.updateImage = this._updateImage.bind(this);
        this._ControlUserEntity = ControlUser;
        this._controlUser = undefined;
        this._systemConfig = systemConfig;
        this._systemConfigEntity = undefined;
        this._actionGroupEntityDef = ActionGroup;
        this._credentialsEntityDef = Credentials;
        this._rolesArray = [];
        this._visibleCred = "";
        this._themeExplorer = {};
        this._pristineColors = {};
        this._defaultColors = DEFAULT_COLORS;
        this._safePalette = SAFE_PALETTE;

        this._getUserProm = this.getUserInfo();
        this._renderer = new Renderer(this, modalUserFeedback);
        this._registerListeners();
        this._storageFactory = storageFactory;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _getMe() {
        return this._userService.logMeInBro({});
    }

    _updateImage(data) {
        this._userImage = data;
        this.emit("UserImage", data);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get router() {
        return this._router;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer._shutdown().then(
            () => {
                this._renderer = undefined;
                this._userImage = undefined;
                this._muf = undefined;
                this._ControlUserEntity = undefined;
                this._controlUser = undefined;
                this._actionGroupEntityDef = undefined;
                this._rolesArray = undefined;
                this._visibleCred = undefined;
            },
            (err) => {
                console.log("Error shutting down.", err);
            }
        );
        return prom;
    }

    getSystemConfig() {
        return this._systemConfig.loadSystemConfig(GSO_ID);
    }

    getUserInfo() {
        return this._getMe()
            .then((info) => {
                this._currentUserData = info;
                return Promise.resolve();
            })
            .then(() => {
                // console.log(this._currentUserData);
                return this._ControlUserEntity
                    .loadControlUser(
                        this._currentUserData.organizationId,
                        this._currentUserData.userId
                    )
                    .then((user) => {
                        // console.log(user);
                        this._controlUser = user;
                        if (_.get(user, "theme", undefined) === undefined) {
                            user.setTheme("LIGHT");
                        }
                        const rotationDelay = _.get(user, "favoriteRotationDelay", -1);
                        if ((!rotationDelay && rotationDelay !== 0) || rotationDelay < 0) {
                            user.setFavoriteRotationDelay(0);
                        }
                        if (_.get(user, "enableFavoriteShortcuts", undefined) === undefined) {
                            user.setEnableFavoriteShortcuts(false);
                        }
                        if (_.get(user, "enableFavoriteHotkeys", undefined) === undefined) {
                            user.setEnableFavoriteHotkeys(false);
                        }
                        if (user.isDirty()) {
                            return user.update().then(() => {
                                this._controlUser = user;
                                return user;
                            });
                        }
                        return user;
                    });
            })
            .then(() => this.getSystemConfig())
            .then((systemConfig) => {
                this._systemConfigEntity = systemConfig;
                return Promise.resolve();
            })
            .then(() => {
                return this.getRoles();
            })
            .then(() => {
                return this._storageFactory.loadUserPreferences();
            })
            .then(() => {
                this._themeStorage = this._storageFactory.getRemoteStorageInstance("user", "theme");
                this._themeExplorerStorage = this._themeStorage.getData("explorer", true);
                if (this._themeExplorerStorage) {
                    this._themeExplorer = this._themeExplorerStorage;
                } else {
                    this._themeExplorer.colors = _.clone(DEFAULT_COLORS);
                }
                this._pristineColors = {
                    hover: this._themeExplorer.colors.hover,
                    selected: this._themeExplorer.colors.selected,
                };
            })
            .catch((err) => {
                this.emit("GetUserError", err);
            });
    }

    get currentUserData() {
        return this._currentUserData;
    }

    get isLdapUser() {
        return _.get(this.currentUserData, "origin", "") === "ldap";
    }

    get isSamlUsersOnly() {
        return _.get(this._systemConfigEntity.getSamlConfig(), "samlUsersOnly", false);
    }

    get isSamlUser() {
        return _.get(this.currentUserData, "origin", "") === "saml";
    }

    get isOauthUsersOnly() {
        return _.get(this._systemConfigEntity.getOauthConfig(), "oauthUsersOnly", false);
    }
    get isOauthUser() {
        return _.get(this.currentUserData, "origin", "") === "oauth";
    }

    get isRadiusUser() {
        return _.get(this.currentUserData, "origin", "") === "radius";
    }

    get isFirstEditable() {
        return !_.get(this.currentUserData, "firstFromIntegration", false);
    }

    get isLastEditable() {
        return !_.get(this.currentUserData, "lastFromIntegration", false);
    }

    get isEmailEditable() {
        return !_.get(this.currentUserData, "emailFromIntegration", false);
    }

    get canAccessDashboard() {
        return _.get(this.currentUserData, "authorizedActions", []).indexOf("dashboardAccess") > -1;
    }

    get canCreateAPICredentials() {
        return (
            _.get(this.currentUserData, "authorizedActions", []).indexOf("createApiCredentials") >
            -1
        );
    }

    whenModelIsReady() {
        return this._getUserProm;
    }

    saveUserBase() {
        if (!_.isEmpty(this._saveModel)) {
            this._saveModel.user.userId = this._currentUserData.userId;

            return this._userService
                .update(this._saveModel.user)
                .then((resp) => {
                    this._currentUserData = resp;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        } else {
            return Promise.resolve();
        }
    }

    saveUserControl() {
        //set image prop in controlUser if needed
        if (this._saveModel.controlUser.userImage !== this._controlUser.image) {
            this._controlUser.setImage(this._saveModel.controlUser.userImage);
        }

        //save all controlUser props
        if (this._controlUser.isDirty()) {
            return this._controlUser.update().catch((err) => {
                this._muf.displayError({
                    errorCode: "Profile Error",
                    errorMsg: "Unable to save the Profile or Profile image.",
                });
                return Promise.reject(err);
            });
        }

        return Promise.resolve();
    }

    saveUserThemes() {
        this._themeStorage.setData(this._themeExplorer, "explorer", true);
        return Promise.resolve();
    }

    clearPreferences() {
        this._storageFactory
            .clearPreferences({userId: this._currentUserData.userId})
            .then(() => {
                this.clearThemeStorage();
                this._storageFactory._isInitialized = false;
                this.emit("SaveSuccess"); //must reset now to get original color changes

                return this._storageFactory.loadUserPreferences();
            })
            .then(() => {
                this.emit("ClearPreferencesSuccess");
            })
            .catch((err) => {
                this.emit("ClearPreferencesError", err);
            });
    }

    clearThemeStorage() {
        this._themeExplorer.colors = _.clone(DEFAULT_COLORS);
    }

    sendTwoFaEmail() {
        this._userService.sendTwoFaEmail(
            {
                userId: this._currentUserData.userId,
            },
            {
                requestHandlers: {
                    Success: () => {
                        this.emit("SendTwoFaEmailSuccess");
                    },
                    Failure: (err) => {
                        this.emit("SendTwoFaEmailError", err);
                    },
                },
            }
        );
    }

    updateUserSaveModelField(property, value) {
        // console.log(this._saveModel);
        if (property === "userImage") {
            _.set(this._saveModel.controlUser, property, value);
        } else {
            _.set(this._saveModel.user, property, value);
        }
        this.emit("SaveModelUpdated");
    }

    removeUserSaveModelField(property) {
        if (property === "userImage") {
            _.unset(this._saveModel.controlUser, property);
        } else {
            _.unset(this._saveModel.user, property);
        }
        if (
            _.isEmpty(this._saveModel.user) &&
            _.isEmpty(this._saveModel.controlUser) &&
            !this._controlUser.isDirty()
        ) {
            this.emit("FormClean");
        } else {
            this.emit("SaveModelUpdated");
        }
    }

    clearUserSaveModel() {
        this._saveModel = {
            user: {},
            controlUser: {},
        };
        this._controlUser.clearPendingPatches();

        return this._storageFactory.loadUserPreferences();
    }

    /*
    resetColorTheme() {
        this._themeExplorer.colors = {
            hover: this._pristineColors.hover,
            selected: this._pristineColors.selected,
        };
    }
     */

    setData() {
        //console.log("ManageMyUser data", data);
    }

    createCredsItem(itemName) {
        const progressCb = (progress) => {
            console.log(progress);
        };
        const updateDateTime = Date.parse(
            new Date(new Date(new Date().addYears(1)).setHours(23, 59, 59))
        );
        const workInput = {name: itemName, expirationDate: updateDateTime};
        this._controlUser
            .runGenerateApiCredentialsWork(workInput, {}, progressCb)
            .then((res) => {
                this._visibleCred = res;
                return this._ControlUserEntity.loadControlUser(
                    this._currentUserData.organizationId,
                    this._currentUserData.userId
                );
            })
            .then((user) => {
                this._controlUser = user;
                this.emit("DataUpdated");
                return Promise.resolve();
            })
            .catch((err) => {
                console.log("Error", err);
            });
    }

    getSlickData() {
        this._slickData = _.values(
            _.get(this._controlUser.getPristineProperties(), "gluwareApiCredentials", {})
        );
        this._slickData.forEach((item, idx) => {
            item.id = idx;
        });
        return this._slickData;
    }

    _updateControlUserEntity(item, updatedField) {
        return this._setControlUserEntity(item, updatedField);
    }

    _setControlUserEntity(item, updatedField) {
        try {
            const name = item.name;
            const expirationDate = new Date(item["expirationDate"]);
            const actionGroupId = item.actionGroupId;

            let apiCredentials = this._controlUser.getGluwareApiCredentials();
            let credentialPair = apiCredentials[item.clientId];

            if (!credentialPair) {
                throw new Error(`Resource ${item.clientId} not found.`);
            }
            if (name && updatedField === "name") {
                credentialPair.name = name;
            }
            if (expirationDate && updatedField === "expirationDate") {
                credentialPair.expirationDate = Date.parse(expirationDate);
            }
            if (actionGroupId && updatedField === "actionGroupId") {
                credentialPair.actionGroupId = actionGroupId;
            }
            apiCredentials[item.clientId] = credentialPair;

            this._controlUser.setGluwareApiCredentials(apiCredentials);
        } catch (err) {
            this.log.error("Error while updating API credentials");
        }
    }

    getRoles() {
        return this._actionGroupEntityDef
            .loadAscendActionGroups(this._currentUserData.organizationId)
            .then((roles) => {
                roles = roles.filter((role) => !role.systemInternal);
                const rolesArray = roles.map((role) =>
                    Object.assign({
                        label: _.get(role, "pristineProperties.name"),
                        value: _.get(role, "id"),
                    })
                );
                this._rolesArray = rolesArray;
                return rolesArray;
            })
            .catch((err) => {
                console.log("getRoles", err);
            });
    }

    deleteCredential(clientId) {
        const workInput = {
            clientId: clientId,
        };

        return this._controlUser.runDeleteApiCredentialsWork(workInput);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = ManageMyUserComponent;

ManageMyUserComponent.$groupName = "User";

ManageMyUserComponent.$displayName = "Edit My Profile";

ManageMyUserComponent.$inject = [
    "$teve.services.instance.User",
    "$teve.ui.UiUtils",
    "$teve.router",
    "$teve.ui.userFeedback",
    "$teve.entities.ControlUser",
    "$teve.storageFactory",
    "$teve.entities.SystemConfig",
    "$teve.entities.ActionGroup",
    "$teve.entities.Credentials",
];
